/**
 * How permissions are PRESENTED: grouped into the modules a manager thinks in.
 *
 * The permission catalogue groups keys by their prefix (`quotations.send` is in
 * `quotations`), which yields thirty-odd categories of one to five keys each —
 * accurate, but too fine to scan. This file folds those categories into a
 * handful of modules for display. It is presentation only: it grants nothing,
 * hides nothing, and the backend never reads it. Every key the catalogue
 * serves is shown, and a category not listed here falls into `other` rather
 * than disappearing (the test next to this file fails if that happens, so a new
 * category is placed deliberately).
 */

export const PERMISSION_MODULES = [
  {
    key: 'administration',
    categories: [
      'settings',
      'branches',
      'departments',
      'numbering',
      'users',
      'permissions',
      'audit',
      'storage',
      'data',
      'dashboard',
    ],
  },
  { key: 'clients', categories: ['clients', 'projects', 'portal_accounts'] },
  {
    key: 'commercial',
    categories: ['catalogue', 'pricing', 'pricing_agreements', 'quotations'],
  },
  {
    key: 'laboratory',
    categories: [
      'work_orders',
      'samples',
      'labels',
      'sample_storage',
      'assignments',
      'files',
    ],
  },
  { key: 'results', categories: ['results', 'reports', 'templates'] },
  {
    key: 'finance',
    categories: ['finance', 'invoices', 'payments', 'statements'],
  },
  { key: 'equipment', categories: ['equipment', 'calibration', 'quality'] },
] as const;

export type PermissionModuleKey =
  | (typeof PERMISSION_MODULES)[number]['key']
  | 'other';

export const ALL_PERMISSION_MODULE_KEYS: readonly PermissionModuleKey[] = [
  ...PERMISSION_MODULES.map((module) => module.key),
  'other',
];

/** The category of a permission key: its prefix, as the catalogue defines it. */
export function categoryOf(key: string): string {
  return key.split('.')[0] ?? 'general';
}

const MODULE_BY_CATEGORY = new Map<string, PermissionModuleKey>(
  PERMISSION_MODULES.flatMap((module) =>
    module.categories.map((category) => [category, module.key] as const),
  ),
);

export function moduleOf(key: string): PermissionModuleKey {
  return MODULE_BY_CATEGORY.get(categoryOf(key)) ?? 'other';
}

export interface PermissionModuleGroup {
  readonly module: PermissionModuleKey;
  /** In catalogue order, so related keys stay together. */
  readonly keys: readonly string[];
}

/**
 * Group permission keys by module, in the order the modules are declared, and
 * keep the order the keys were given within each module. Modules with no keys
 * are omitted unless `includeEmpty` is set.
 */
export function groupByModule(
  keys: readonly string[],
  { includeEmpty = false }: { includeEmpty?: boolean } = {},
): PermissionModuleGroup[] {
  const buckets = new Map<PermissionModuleKey, string[]>(
    ALL_PERMISSION_MODULE_KEYS.map((module) => [module, []]),
  );
  for (const key of keys) buckets.get(moduleOf(key))?.push(key);

  return ALL_PERMISSION_MODULE_KEYS.map((module) => ({
    module,
    keys: buckets.get(module) ?? [],
  })).filter((group) => includeEmpty || group.keys.length > 0);
}
